import { useEffect, useState } from "react";
import { receivablesAPI } from "../../apis/ReceivablesAPI.jsx";

/* Adapter hook for the Receive tab. Reshapes the BACKEND_API_SPEC §2.1
   channel object and §2.4 payment object into the ReceiveScreen prop
   contract.

   Falls back to the inline MOCK_CHANNELS that ReceiveScreen ships with
   if the API is unreachable or the receivables_v1 flag is off (404). */

const PAYMENT_RAIL = { bank: "Wire", payme: "CARD" };
const ONCHAIN_TICKERS = new Set(["USDC", "USDT", "BTC", "ETH"]);
const STATUS = { settled: "CLEARED", pending: "PENDING", returned: "RETURNED" };

function railForPayment(p) {
  const r = String(p.rail || "").toLowerCase();
  if (PAYMENT_RAIL[r]) return PAYMENT_RAIL[r];
  if (r === "crypto" || r === "wallet_transfer") {
    const cur = String(p.currency || "").toUpperCase();
    return ONCHAIN_TICKERS.has(cur) ? cur : "USDC";
  }
  return "Wire";
}

/* relTime — human "2m ago / 1h 22m / 12d ago" to match the mock. */
function relTime(iso) {
  if (!iso) return "—";
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return "—";
  const diff = Math.max(0, Date.now() - ts);
  const s = Math.floor(diff / 1000);
  if (s < 60)  return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  { const rem = m % 60; return rem ? `${h}h ${rem}m` : `${h}h ago`; }
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/* Build accounts[] from §2.1 fields. One channel can carry up to three
   surfaces (bank trio / deposit_addresses[] / pay_link_url) — render
   each as its own row in the design's RECEIVING ACCOUNTS list. */
function deriveAccounts(c) {
  const channelId = c.channel_id || c.id;
  const settlementCcy = c.settlement_currency || (c.currencies_in || [])[0] || "USD";
  const rails = c.rails || [];
  const accounts = [];

  if (c.deposit_account_number || rails.includes("bank")) {
    const last4 = c.deposit_account_number ? c.deposit_account_number.slice(-4) : null;
    accounts.push({
      id:            `${channelId}-bank`,
      kind:          "BANK",
      currency:      settlementCcy,
      label:         `${settlementCcy} · ${last4 ? "Bank" : "Provisioning"}`,
      institution:   c.deposit_account_name || "Bloxcross",
      number_masked: last4 ? `·••• ${last4}` : "Provisioning…",
      routing:       c.deposit_routing || "Provisioning…",
      balance:       "0",
      primary:       true,
    });
  }
  (c.deposit_addresses || []).forEach((a, i) => {
    const addr = a.address || "";
    accounts.push({
      id:            `${channelId}-crypto-${i}`,
      kind:          "WALLET",
      currency:      "USDC",
      label:         `${a.chain || "On-chain"} · USDC`,
      institution:   "On-chain",
      number_masked: addr ? `${addr.slice(0, 6)}·••• ${addr.slice(-4)}` : "—",
      routing:       a.chain || "—",
      balance:       "0",
    });
  });
  if (c.pay_link_url || rails.includes("payme")) {
    const slug = c.pay_link_url ? c.pay_link_url.split("/").pop() : null;
    accounts.push({
      id:            `${channelId}-paylink`,
      kind:          "CARD",
      currency:      settlementCcy,
      label:         "Pay link · card",
      institution:   "Pay.blox",
      number_masked: slug || "Provisioning…",
      routing:       "card",
      balance:       "0",
    });
  }
  if (accounts.length === 0) {
    /* No rail-specific data yet — synthesize a single primary so the
       three-pane layout stays intact. */
    accounts.push({
      id:            `${channelId}-A1`,
      kind:          "BANK",
      currency:      settlementCcy,
      label:         `${settlementCcy} account`,
      institution:   "Provisioning",
      number_masked: "·••• ••••",
      routing:       "—",
      balance:       "0",
      primary:       true,
    });
  }
  return accounts;
}

export default function useTerminalReceive(token) {
  const [channels, setChannels] = useState(null);  // null = use ReceiveScreen's mock
  const [incoming, setIncoming] = useState({});
  const [status, setStatus] = useState(token ? "loading" : "fallback");

  useEffect(() => {
    if (!token) { setStatus("fallback"); return; }
    let cancelled = false;

    const tick = async () => {
      try {
        const list = await receivablesAPI.listChannels(token, { size: 50 });
        if (cancelled) return;
        const body = list?.data;
        if (body && body.code !== 0 && body.code != null) {
          /* Application-level error (e.g., 1000203 needs verification);
             let the design mock show instead of an empty pane. */
          setStatus("degraded");
          setChannels(null); setIncoming({});
          return;
        }
        const arr = Array.isArray(body?.content) ? body.content
                  : Array.isArray(body)         ? body
                  : Array.isArray(body?.items)  ? body.items
                  : null;
        if (!arr || arr.length === 0) {
          setStatus("live");
          setChannels(null); setIncoming({});
          return;
        }
        const normalized = arr.map((c) => ({
          id:                 c.channel_id || c.id,
          client:             c.name || c.client_id || "—",
          region_iso:         "",
          status:             String(c.status || "active").toUpperCase(),
          label:              c.client_email || (c.currencies_in || []).join(", ") || "",
          note:               c.auto_consolidate ? `Auto-consolidate → ${c.settlement_currency || "USD"}` : "",
          vol_30d_usd_eq:     c.total_received_usd || "0",
          received_30d_count: c.payment_count ?? 0,
          last_seen_iso:      relTime(c.last_payment_at),
          accounts:           deriveAccounts(c),
        }));

        /* Lazy-load incoming for the first 5 channels so first paint
           doesn't fan out 50 requests; the rest fill in as the user
           clicks. */
        const incomingMap = {};
        const top = normalized.slice(0, 5);
        await Promise.all(top.map(async (ch) => {
          try {
            const p = await receivablesAPI.listChannelPayments(token, ch.id, { size: 10 });
            const pb = p?.data;
            const items = Array.isArray(pb?.content) ? pb.content
                        : Array.isArray(pb) ? pb : [];
            incomingMap[ch.id] = items.map((tx) => ({
              rail:          railForPayment(tx),
              currency:      tx.currency || ch.accounts[0]?.currency || "USD",
              amount:        tx.amount || "0",
              status:        STATUS[String(tx.status || "").toLowerCase()] || "PENDING",
              when:          String(tx.settled_at || "").slice(11, 16),
              reference:     tx.memo || tx.payment_id?.slice(-8) || "",
              account_label: tx.from_label || ch.accounts[0]?.label || "",
            }));
          } catch (_) { incomingMap[ch.id] = []; }
        }));
        if (cancelled) return;
        setChannels(normalized);
        setIncoming(incomingMap);
        setStatus("live");
      } catch (e) {
        if (cancelled) return;
        const code = e?.response?.status;
        setStatus(code === 404 ? "feature_off" : "degraded");
        setChannels(null);
        setIncoming({});
      }
    };

    tick();
    const intervalId = setInterval(tick, 30_000);
    return () => { cancelled = true; clearInterval(intervalId); };
  }, [token]);

  return { channels, incoming, status };
}
